import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  GoogleMap,
  DirectionsRenderer,
  useJsApiLoader,
} from "@react-google-maps/api";
import axios from "axios";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import { useCombinedStore } from "../../../store/hooks/useCombinedStore";
import GlobalSpinner from "../../CustomReusables/GlobalSpinner";
import { PickDenguShareButton } from "../../Businesses/BusinessQrShareActions";

const fallbackCenter = { lat: -13.9626, lng: 33.7741 };
const cardsBorderRadii ="8px";

const getApiBaseUrl = () => {
  const isBrowser = typeof window !== "undefined";
  const isLocalhost =
    isBrowser &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  const localBaseUrl = process.env.REACT_APP_BACKEND_URL;
  const publicBaseUrl =
    process.env.REACT_APP_PUBLIC_API_BASE_URL ||
    process.env.REACT_APP_API_BASE_URL;

  const normalizeBaseUrl = (value: string) => {
    const cleanBaseUrl = value.replace(/\/+$/, "");

    return cleanBaseUrl.endsWith("/api/v1")
      ? cleanBaseUrl
      : `${cleanBaseUrl}/api/v1`;
  };

  if (isLocalhost) {
    return localBaseUrl?.trim()
      ? normalizeBaseUrl(localBaseUrl)
      : "http://localhost:5000/api/v1";
  }

  if (publicBaseUrl?.trim()) {
    return normalizeBaseUrl(publicBaseUrl);
  }

  if (
    localBaseUrl?.trim() &&
    !localBaseUrl.includes("localhost") &&
    !localBaseUrl.includes("127.0.0.1")
  ) {
    return normalizeBaseUrl(localBaseUrl);
  }

  return "https://api.pickdengu.mw/api/v1";
};

const HIKES_API = `${getApiBaseUrl()}/hike-trips`;

const PICKDENGU_PUBLIC_WEB_URL = (
  process.env.REACT_APP_PUBLIC_WEB_URL ||
  process.env.REACT_APP_FRONTEND_URL ||
  process.env.REACT_APP_APP_URL ||
  "https://pickdengu.mw"
).replace(/\/+$/, "");

const HikeDetailsPage = () => {
  const { theme } = useCustomTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation() as { state?: { hike?: any } };
  const combinedStore = useCombinedStore() as any;

  const hikes = Array.isArray(combinedStore?.hikes) ? combinedStore.hikes : [];
  const [loading, setLoading] = useState(true);
  const [directions, setDirections] =
    useState<google.maps.DirectionsResult | null>(null);
  const [fetchedHike, setFetchedHike] = useState<any | null>(null);

  const matchesHikeId = (value: any, currentId?: string) => {
    if (!value || !currentId) return false;
    return String(value?._id ?? value?.id) === String(currentId);
  };

  const hikeFromStore = useMemo(
    () => hikes.find((h: any) => matchesHikeId(h, id)),
    [hikes, id],
  );

  const hikeFromState = useMemo(() => {
    return matchesHikeId(state?.hike, id) ? state?.hike : null;
  }, [state?.hike, id]);

  const hikeFromFetch = useMemo(() => {
    return matchesHikeId(fetchedHike, id) ? fetchedHike : null;
  }, [fetchedHike, id]);

  const isRichHike = (value: any) => {
    if (!value) return false;

    return Boolean(
      value?.from ||
      value?.to ||
      value?.fromAddress ||
      value?.toAddress ||
      value?.origin ||
      value?.destination ||
      value?.date ||
      value?.departDate ||
      value?.departTime ||
      value?.departureTime ||
      value?.price != null ||
      value?.priceMk != null ||
      value?.pricePerSeat != null ||
      value?.availableSeats != null ||
      value?.seatsAvailable != null ||
      value?.seats != null,
    );
  };

  const hike = useMemo(() => {
    if (isRichHike(hikeFromFetch)) return hikeFromFetch;
    if (isRichHike(hikeFromState)) return hikeFromState;
    if (isRichHike(hikeFromStore)) return hikeFromStore;

    return hikeFromFetch ?? hikeFromState ?? hikeFromStore ?? null;
  }, [hikeFromFetch, hikeFromState, hikeFromStore]);

  useEffect(() => {
    setFetchedHike(null);
    setDirections(null);
    setLoading(true);
  }, [id]);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    const alreadyHaveHike =
      isRichHike(hikeFromState) ||
      isRichHike(hikeFromStore) ||
      isRichHike(hikeFromFetch);

    if (alreadyHaveHike) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const run = async () => {
      setLoading(true);

      try {
        const res = await axios.get(`${HIKES_API}/${id}`);
        const fullHike =
          res.data?.data ||
          res.data?.trip ||
          res.data?.hike ||
          res.data ||
          null;

        if (isMounted && matchesHikeId(fullHike, id)) {
          setFetchedHike(fullHike);
        } else if (isMounted) {
          setFetchedHike(null);
        }
      } catch (error) {
        console.error("Failed to fetch hike details:", error);
        if (isMounted) {
          setFetchedHike(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [id, hikeFromState, hikeFromStore, hikeFromFetch]);

  const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
    libraries: ["places"],
  });

  const mapRef = useRef<google.maps.Map | null>(null);

  const fromLocation =
    hike?.from?.address ||
    hike?.from ||
    hike?.fromAddress ||
    hike?.origin ||
    "";

  const toLocation =
    hike?.to?.address || hike?.to || hike?.toAddress || hike?.destination || "";

  useEffect(() => {
    if (!isLoaded) return;
    if (!fromLocation || !toLocation) return;
    if (!(window as any).google?.maps?.DirectionsService) return;

    const service = new window.google.maps.DirectionsService();

    service.route(
      {
        origin: fromLocation,
        destination: toLocation,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK" && result) {
          setDirections(result);
        } else {
          setDirections(null);
        }
      },
    );
  }, [isLoaded, fromLocation, toLocation]);

  const center = useMemo(() => {
    const coords =
      hike?.fromCoords ||
      hike?.pickupLocationCoords ||
      hike?.from?.coords ||
      (hike?.from?.lat != null && hike?.from?.lng != null
        ? { lat: hike.from.lat, lng: hike.from.lng }
        : null);

    if (coords?.lat && coords?.lng) return coords;
    return fallbackCenter;
  }, [hike]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    const t = window.setTimeout(() => {
      try {
        // @ts-ignore
        window.google?.maps?.event?.trigger(mapRef.current, "resize");
        mapRef.current?.panTo(center);
      } catch {}
    }, 180);

    return () => window.clearTimeout(t);
  }, [isLoaded, center]);

  const availableSeats = Number(
    hike?.availableSeats ?? hike?.seatsAvailable ?? hike?.seats ?? 0,
  );

  const price = Number(hike?.priceMk ?? hike?.price ?? hike?.pricePerSeat ?? 0);

  const tripDateRaw = hike?.departDate || hike?.date || null;
  const dateLabel = tripDateRaw
    ? new Date(tripDateRaw).toLocaleDateString()
    : "-";
  const departTime = hike?.departTime || hike?.departureTime || "-";
  const arriveTime = hike?.arriveTime || hike?.arrivalTime || "-";
  const driverName = hike?.driverName || hike?.driver?.name || "Driver";
  const carType = hike?.carType || hike?.car?.type || "-";
  const description = hike?.description || "No trip description provided.";

  const tripDateTime = useMemo(() => {
    if (!tripDateRaw) return null;

    const baseDate = new Date(tripDateRaw);
    if (Number.isNaN(baseDate.getTime())) return null;

    const timeValue = departTime && departTime !== "-" ? departTime : "00:00";
    const [rawHours, rawMinutes] = String(timeValue).split(":");
    const hours = Number(rawHours);
    const minutes = Number(rawMinutes);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return baseDate;
    }

    const combined = new Date(baseDate);
    combined.setHours(hours, minutes, 0, 0);
    return combined;
  }, [tripDateRaw, departTime]);

  const isFutureTrip = tripDateTime
    ? tripDateTime.getTime() > Date.now()
    : false;
  const canBook = availableSeats > 0 && isFutureTrip;

  const hikeShareId = String(hike?._id || hike?.id || id || "").trim();

  const hikeShareName = `${fromLocation || "From"} to ${toLocation || "To"}`;

  const hikeShareUrl = hikeShareId
    ? `${PICKDENGU_PUBLIC_WEB_URL}/hikes/${encodeURIComponent(hikeShareId)}`
    : PICKDENGU_PUBLIC_WEB_URL;

  const hikeShareText = [
    hikeShareName,
    dateLabel,
    departTime !== "-" ? `Depart ${departTime}` : "",
    `MWK ${price.toLocaleString()} per seat`,
    `${availableSeats} seats left`,
    description?.trim()?.slice(0, 160),
  ]
    .filter(Boolean)
    .join(" • ");

  if (loading) {
    return (
      <Box p={6} color={theme.text}>
        <HStack>
          <GlobalSpinner />
          <Text>Loading hike details...</Text>
        </HStack>
      </Box>
    );
  }

  if (!hike) {
    return (
      <Box p={6} color={theme.text}>
        <Box
          border="1px solid"
          borderColor={theme.border}
          bg={theme.surface}
          borderRadius={cardsBorderRadii}
          p={5}
          boxShadow={theme.shadowSm}
        >
          <Text mb={4} color={theme.text}>
            No hike details found.
          </Text>
          <Button
            onClick={() => navigate(-1)}
            color={theme.textOnPrimary}
            bgImage={theme.gradients.primaryButton}
            _hover={{ bgImage: theme.gradients.primaryButtonHover }}
            boxShadow={theme.shadowSm}
          >
            Back
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }} color={theme.text}>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between" align="start" flexWrap="wrap">
          <VStack align="start" spacing={1}>
            <Text
              fontSize={{ base: "2xl", md: "3xl" }}
              fontWeight="bold"
              color={theme.text}
            >
              {fromLocation || "From"} → {toLocation || "To"}
            </Text>
            <Text color={theme.textSubtle}>
              {dateLabel} • {departTime}{" "}
              {arriveTime !== "-" ? `• Arrive ${arriveTime}` : ""}
            </Text>
          </VStack>

          <HStack spacing={2} flexWrap="wrap">
            <Badge colorScheme={availableSeats > 0 ? "green" : "red"}>
              {availableSeats > 0 ? `${availableSeats} seats left` : "Full"}
            </Badge>
            <Badge colorScheme={isFutureTrip ? "purple" : "orange"}>
              {isFutureTrip ? "Upcoming Trip" : "Past Trip"}
            </Badge>
          </HStack>
        </HStack>

        <SimpleGrid
          columns={{ base: 1, lg: 2 }}
          spacing={6}
          alignItems="stretch"
        >
          <VStack align="stretch" spacing={4} h="100%">
            <Box
              borderWidth="1px"
              borderColor={theme.border}
              borderRadius={cardsBorderRadii}
              bg={theme.surface}
              p={4}
              boxShadow={theme.shadowSm}
              h="100%"
            >
              <Text fontSize="xl" fontWeight="bold" color={theme.text} mb={2}>
                MWK {price.toLocaleString()}
                <Text
                  as="span"
                  ml={2}
                  fontSize="sm"
                  fontWeight="normal"
                  color={theme.textSubtle}
                >
                  / seat
                </Text>
              </Text>

              <Text color={theme.textMuted} mb={4}>
                {description}
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <Info label="From" value={fromLocation || "-"} />
                <Info label="To" value={toLocation || "-"} />
                <Info label="Date" value={dateLabel} />
                <Info label="Depart Time" value={departTime} />
                <Info label="Arrive Time" value={arriveTime} />
                <Info label="Seats Left" value={String(availableSeats)} />
                <Info label="Driver" value={driverName} />
                <Info label="Car Type" value={carType} />
              </SimpleGrid>

              <Divider borderColor={theme.divider} my={4} />

              <VStack align="stretch" spacing={3}>
                {!isFutureTrip && (
                  <Text fontSize="sm" color={theme.warning || theme.textSubtle}>
                    This trip is in the past and can no longer be booked.
                  </Text>
                )}

                {isFutureTrip && availableSeats <= 0 && (
                  <Text fontSize="sm" color={theme.danger}>
                    This trip is full and cannot be booked right now.
                  </Text>
                )}

                <HStack flexWrap="wrap">
                  <Button
                    w={{ base: "100%", sm: "160px" }}
                    color={theme.textOnPrimary}
                    bgImage={theme.gradients.primaryButton}
                    _hover={{ bgImage: theme.gradients.primaryButtonHover }}
                    boxShadow={theme.shadowSm}
                    isDisabled={!canBook}
                    onClick={() =>
                      navigate(`/hike/checkout/${hike?._id ?? hike?.id}`, {
                        state: { trip: hike, hike },
                      })
                    }
                    whiteSpace="nowrap"
                  >
                    Book
                  </Button>

                  <PickDenguShareButton
                    shareTitle={`${hikeShareName} on PickDengu`}
                    shareText={hikeShareText}
                    shareUrl={hikeShareUrl}
                    modalTitle={`Share ${hikeShareName}`}
                    modalDescription="Share this trip details page."
                    buttonProps={{
                      w: {
                        base: "100%",
                        sm: "130px",
                      },
                      variant: "outline",
                      borderColor: theme.borderStrong,
                      color: theme.text,
                      bg: theme.surface,
                      boxShadow: theme.shadowSm,
                      borderRadius: {cardsBorderRadii},
                      _hover: {
                        bg: theme.hover,
                      },
                    }}
                  />

                  <Button
                    w={{ base: "100%", sm: "auto" }}
                    onClick={() => navigate(-1)}
                    variant="outline"
                    borderColor={theme.borderStrong}
                    color={theme.text}
                    _hover={{ bg: theme.hover }}
                  >
                    Back
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </VStack>

          <VStack align="stretch" spacing={4} h="100%">
            <Box
              borderWidth="1px"
              borderColor={theme.border}
              borderRadius={cardsBorderRadii}
              overflow="hidden"
              bg={theme.surface}
              boxShadow={theme.shadowSm}
              h="100%"
              display="flex"
              flexDirection="column"
            >
              <Box
                px={3}
                py={2}
                bg={theme.surfaceAlt}
                borderBottomWidth="1px"
                borderColor={theme.border}
              >
                <Text fontWeight="semibold" color={theme.text}>
                  Route Map
                </Text>
                <Text fontSize="sm" color={theme.textMuted}>
                  {fromLocation || "—"} → {toLocation || "—"}
                </Text>
              </Box>

              {!API_KEY ? (
                <Flex p={3} flex="1" align="center" justify="center">
                  <Text fontSize="sm" color={theme.danger}>
                    Missing REACT_APP_GOOGLE_MAPS_API_KEY — cannot load map.
                  </Text>
                </Flex>
              ) : loadError ? (
                <Flex p={3} flex="1" align="center" justify="center">
                  <Text fontSize="sm" color={theme.danger}>
                    Maps failed to load.
                  </Text>
                </Flex>
              ) : !isLoaded ? (
                <Flex
                  align="center"
                  justify="center"
                  flex="1"
                  minH={{ base: "320px", lg: "0" }}
                  gap={2}
                >
                  <GlobalSpinner />
                  <Text fontSize="sm" color={theme.textSubtle}>
                    Loading map…
                  </Text>
                </Flex>
              ) : (
                <Box flex="1" minH={{ base: "320px", lg: "0" }} w="100%">
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={center}
                    zoom={13}
                    options={{
                      disableDefaultUI: true,
                      zoomControl: true,
                      clickableIcons: false,
                    }}
                    onLoad={(map) => {
                      mapRef.current = map;
                    }}
                    onUnmount={() => {
                      mapRef.current = null;
                    }}
                  >
                    {directions && (
                      <DirectionsRenderer
                        directions={directions}
                        options={{ suppressMarkers: false }}
                      />
                    )}
                  </GoogleMap>
                </Box>
              )}
            </Box>
          </VStack>
        </SimpleGrid>
      </VStack>
    </Box>
  );
};

function Info({ label, value }: { label: string; value?: string }) {
  const { theme } = useCustomTheme();

  return (
    <Box
      borderWidth="1px"
      borderColor={theme.border}
      borderRadius={cardsBorderRadii}
      p={3}
      bg={theme.surface}
    >
      <Text fontSize="xs" color={theme.textSubtle}>
        {label}
      </Text>
      <Text fontWeight="semibold" color={theme.text}>
        {value || "—"}
      </Text>
    </Box>
  );
}

export default HikeDetailsPage;
